import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface CountdownTimerProps {
  /** ISO 8601 timestamp — time at which the countdown hits zero. */
  targetTime: string;
  /**
   * Optional render override. Receives remaining ms and whether we've
   * crossed the target. Useful for custom chrome in FeedView cards.
   */
  renderLabel?: (ms: number, expired: boolean) => React.ReactNode;
  /** Custom "expired" label; defaults to i18n `runs.participant.feed.timesUp`. */
  expiredLabel?: React.ReactNode;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(mins)}:${pad(secs)}`;
}

/**
 * Live countdown display. Re-renders every second (only while mounted
 * and while the target is still in the future — once expired, the
 * interval clears so we don't churn).
 */
const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetTime,
  renderLabel,
  expiredLabel,
}) => {
  const { t } = useTranslation('runs');
  const targetMs = new Date(targetTime).getTime();
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, targetMs - Date.now()),
  );

  useEffect(() => {
    if (targetMs - Date.now() <= 0) return;

    const id = setInterval(() => {
      const left = targetMs - Date.now();
      setRemaining(Math.max(0, left));
      if (left <= 0) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [targetMs]);

  const expired = remaining <= 0;

  if (renderLabel) {
    return <>{renderLabel(remaining, expired)}</>;
  }

  return (
    <Box role="timer" aria-live="polite">
      <Typography
        component="span"
        sx={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          fontFamily: 'monospace',
        }}
      >
        {expired ? (expiredLabel ?? t('participant.feed.timesUp')) : formatRemaining(remaining)}
      </Typography>
    </Box>
  );
};

export default CountdownTimer;
